import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Role, type MachineClientResponse } from '../../api';
import {
  AsyncSection,
  Form,
  FormError,
  PageHeader,
  Panel,
  SubmitButton,
  TextField,
  fieldErrors,
  useNotifications,
} from '../../components';
import { PATHS } from '../../routes/paths';
import { useCompanies, useIssueMachineClient } from '../queries';
import { CREDENTIALS_COPY, ROLE_LABELS, type RevealedSecret } from './credentials';

/**
 * Issuing a machine credential for one customer company.
 *
 * Its own screen, so the list next door answers "which integrations can call
 * the API?" without a form open over the answer. Two decisions shape it, and
 * the second is why this screen does not simply bounce back to the list the
 * moment the request succeeds:
 *
 * **The company is chosen, never typed.** A credential reaches exactly one
 * customer company for its whole life, and picking the wrong one is not a
 * mistake a screen should make available. The select is populated from the
 * companies this staff member can already see, and the choice travels as the
 * `X-Company-Id` scope header rather than in the body -- so a credential cannot
 * name a company the request is not for.
 *
 * **The secret is shown once, here, and the way onwards is the button that
 * dismisses it.** It is returned by the issue response and exists nowhere else:
 * not in the list, not in the audit trail, not in our database in a readable
 * form. Navigating away automatically would destroy it while somebody is still
 * looking for their password manager, so the screen stops and waits. Pressing
 * "já guardei o segredo" is what returns to the list, where the new credential
 * is waiting.
 */
export function NewCredentialPage() {
  const companies = useCompanies();
  const issue = useIssueMachineClient();
  const navigate = useNavigate();
  const { notify } = useNotifications();

  const [companyId, setCompanyId] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<string>(Role.MACHINE_WRITE);
  const [revealed, setRevealed] = useState<RevealedSecret | null>(null);

  const issueErrors = fieldErrors(issue.error);

  const submit = () => {
    issue.mutate(
      { companyId, name, role: role as MachineClientResponse['role'] },
      {
        onSuccess: (created) => {
          setRevealed({
            clientId: created.client_id,
            clientSecret: created.client_secret,
          });
          notify('success', CREDENTIALS_COPY.issued);
        },
      },
    );
  };

  if (revealed !== null) {
    return (
      <div className="page">
        <PageHeader title={CREDENTIALS_COPY.issued} description={CREDENTIALS_COPY.description} />

        <Panel title={CREDENTIALS_COPY.issued} description={CREDENTIALS_COPY.secretDescription}>
          <dl className="definitions">
            <div>
              <dt>client_id</dt>
              <dd>
                <code>{revealed.clientId}</code>
              </dd>
            </div>
            <div>
              <dt>client_secret</dt>
              <dd>
                <code>{revealed.clientSecret}</code>
              </dd>
            </div>
          </dl>
          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              void navigate(PATHS.staffCredentials);
            }}
          >
            {CREDENTIALS_COPY.secretKept}
          </button>
        </Panel>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title={CREDENTIALS_COPY.issueTitle}
        description={CREDENTIALS_COPY.issueDescription}
        actions={
          <Link className="button button--ghost" to={PATHS.staffCredentials}>
            {CREDENTIALS_COPY.back}
          </Link>
        }
      />

      <Panel title={CREDENTIALS_COPY.issueTitle} description={CREDENTIALS_COPY.issueDescription}>
        <AsyncSection
          state={companies}
          loadingLabel="Carregando empresas…"
          errorTitle="Não foi possível carregar as empresas"
          isEmpty={(data) => data.items.length === 0}
          emptyTitle="Nenhuma empresa cadastrada"
          emptyDescription="Uma credencial pertence a uma empresa cliente, então é preciso cadastrar uma antes."
          emptyAction={
            <Link className="button button--secondary" to={PATHS.staffCompanyNew}>
              Cadastrar empresa
            </Link>
          }
        >
          {(data) => (
            <Form onSubmit={submit} aria-label={CREDENTIALS_COPY.issueFormLabel}>
              <FormError error={issue.error} />
              <div className="field">
                <label className="field__label" htmlFor="credential-company">
                  Empresa cliente
                  <span className="field__required" aria-hidden="true">
                    *
                  </span>
                </label>
                <select
                  id="credential-company"
                  className="field__input"
                  value={companyId}
                  required
                  onChange={(event) => setCompanyId(event.target.value)}
                >
                  <option value="">Escolha uma empresa…</option>
                  {data.items.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              <TextField
                label="Nome da integração"
                value={name}
                onChange={setName}
                required
                hint="Para reconhecer esta credencial depois, no log e nesta tela."
                {...(issueErrors['name'] !== undefined && { error: issueErrors['name'] })}
              />

              <div className="field">
                <label className="field__label" htmlFor="credential-role">
                  Permissão
                </label>
                <select
                  id="credential-role"
                  className="field__input"
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                >
                  <option value={Role.MACHINE_READ}>{ROLE_LABELS[Role.MACHINE_READ]}</option>
                  <option value={Role.MACHINE_WRITE}>{ROLE_LABELS[Role.MACHINE_WRITE]}</option>
                </select>
              </div>

              <div className="form__actions">
                <SubmitButton pending={issue.isPending} pendingLabel={CREDENTIALS_COPY.issuing}>
                  {CREDENTIALS_COPY.issue}
                </SubmitButton>
                <Link className="button button--ghost" to={PATHS.staffCredentials}>
                  {CREDENTIALS_COPY.cancel}
                </Link>
              </div>
            </Form>
          )}
        </AsyncSection>
      </Panel>
    </div>
  );
}
